import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faCircleInfo,
    faFileInvoice,
    faEuroSign,
    faCalendar,
    faUser,
    faEye,
    faDownload,
    faEnvelope,
} from "@fortawesome/free-solid-svg-icons";

import RegisterChargePopup from "../../../components/charge/RegisterChargePopUp";
import PaginatorComponent from "../../../components/general/auxiliar/ui/PaginatorComponent";
import CustomForm from "../../../components/general/form/CustomForm";
import Collapse from "../../../components/general/auxiliar/collapse/Collapse";
import EmptyTable from "../../../components/general/auxiliar/table/EmptyTable";
import LoadingTable from "../../../components/general/auxiliar/table/LoadingTable";
import OverviewTable from "../../../components/general/auxiliar/table/OverviewTable";
import OverviewCardList from "../../../components/general/auxiliar/table/OverviewCard";
import TableBadge from "../../../components/general/auxiliar/table/TableBadge";
import Dropdown from "../../../components/general/auxiliar/ui/Dropdown";
import useFormWithDisable from "../../../auxiliar/customHooks/useFormWithDisable";
import usePaginationFilterEffects from "../../../auxiliar/customHooks/usePaginationFilterEffects";
import {
    handleSubmit,
    handleSubmitWithDispatch,
    getTableState,
} from "../../../auxiliar/auxFunctions";
import { isDefined } from "../../../auxiliar/formatValidators";
import { getAllCashRegisters, downloadCashRegisters } from "../../../actions/cashRegister";
import { downloadInvoice, sendInvoiceEmail, postInvoice } from "../../../actions/invoice";

// Shared dropdown renderer
const renderCashRegisterDropdown = (
    cashRegister,
    id,
    triggerSize = "sm",
    navigate,
    dispatch,
    disabled,
    refreshBool,
    setRefreshBool,
    clinicId,
) => {
    const dropdownItems = createCashRegisterDropdownItems({
        cashRegister,
        id,
        navigate,
        dispatch,
        disabled,
        refreshBool,
        setRefreshBool,
        clinicId,
    });

    const trigger = (
        <div className={`btn btn-primary btn-${triggerSize}`}>
            <FontAwesomeIcon icon={faChevronDown} className="text-base-100" />
            {triggerSize === "sm" && <span className="hidden xl:inline"> Opciones</span>}
        </div>
    );

    return (
        <Dropdown
            trigger={trigger}
            items={dropdownItems}
            position="bottom-right"
            width="w-52"
            closeOnItemClick={true}
        />
    );
};

// Helper function to get form configuration
const getFormConfig = (writtenFilter) => ({
    patient_name: {
        condition: true,
        disabled: false,
        type: "text",
        label: "Nombre del paciente",
        placeholder: "Nombre",
        name: "patient_name",
    },
    patient_surnames: {
        condition: true,
        disabled: false,
        type: "text",
        label: "Apellidos del paciente",
        placeholder: "Apellidos",
        name: "patient_surnames",
    },
    employee_id: {
        condition: true,
        disabled: false,
        allowAll: true,
        type: "physio_selector",
        label: "Fisioterapeuta",
        name: "employee_id",
    },
    payment_method: {
        condition: true,
        disabled: false,
        allowAll: true,
        type: "payment_method_selector",
        label: "Método de pago",
        name: "payment_method",
        info: "Se mostrarán los albaranes que tienen al menos un pago con el método seleccionado",
    },
    temporality: {
        condition: true,
        disabled: false,
        type: "selector",
        name: "temporality",
        label: "Temporalidad",
        options: [
            { key: "all", label: "Todas" },
            { key: "current", label: "Actuales" },
            { key: "future", label: "Posteriores a la fecha actual" },
            { key: "past", label: "Anteriores a la fecha actual" },
            { key: "custom", label: "Filtro personalizado" },
        ],
        defaultValue: "all",
    },
    date_to_filter: {
        condition: ["current", "future", "past", "custom"].includes(writtenFilter.temporality),
        disabled: false,
        type: "selector",
        name: "date_to_filter",
        label: "Fecha a filtrar *",
        options: [
            { key: "entity_date", label: "Fecha del servicio" },
            { key: "issue_date", label: "Fecha de creación" },
        ],
        defaultValue: "entity_date",
    },
    period: {
        condition: ["future", "past", "current"].includes(writtenFilter.temporality),
        disabled: false,
        type: "selector",
        name: "period",
        label: "Periodo temporal",
        options: [
            { key: "days", label: "Día" },
            { key: "weeks", label: "Semana" },
            { key: "months", label: "Mes" },
            { key: "years", label: "Año" },
        ],
        defaultValue: "days",
    },
    duration: {
        condition: ["future", "past"].includes(writtenFilter.temporality),
        disabled: false,
        type: "number",
        label: "Duración",
        placeholder: "Ilimitado",
        name: "duration",
        info: "Número de días, semanas, meses o años que quieres que dure el filtro. Si no lo rellenas, se tomará como ilimitado y se ignorará el periodo temporal",
        minValue: 1,
    },
    starting_date: {
        condition: writtenFilter.temporality === "custom",
        disabled: false,
        type: "date",
        label: "Fecha incial",
        name: "starting_date",
    },
    finishing_date: {
        condition: writtenFilter.temporality === "custom",
        disabled: false,
        type: "date",
        label: "Fecha final",
        name: "finishing_date",
    },
    dummy: {
        condition: ["all", "current"].includes(writtenFilter.temporality),
        type: "dummy",
    },
    unpaid: {
        condition: true,
        disabled: false,
        label: "Excluir pagados",
        info: "Si lo seleccionas, solo se mostrarán los albaranes que todavía no han sido pagados completamente",
        type: "checkbox",
        name: "unpaid",
    },
    has_invoice: {
        condition: true,
        disabled: false,
        label: "Factura asociada",
        info: "Si lo seleccionas, solo se mostrarán los albaranes con factura ya creada",
        type: "checkbox",
        name: "has_invoice",
    },
});

const OverviewCashRegister = () => {
    const { register, watch, errors } = useFormWithDisable();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id: clinicId } = useSelector((state) => state.activeClinic);

    const [cashRegisters, setCashRegisters] = useState({ waiting: true });
    const [refreshBool, setRefreshBool] = useState(false);
    const [filter, setFilter] = useState({
        clinic_id: clinicId,
        page: 1,
        per_page: 10,
        order: "issue_date",
    });

    const writtenFilter = {
        order: watch("date_to_filter") || "issue_date",
        patient_name: watch("patient_name") || "",
        patient_surnames: watch("patient_surnames") || "",
        employee_id: watch("employee_id") || "all",
        payment_method: watch("payment_method") || "all",
        temporality: watch("temporality") || "all",
        date_to_filter: watch("date_to_filter") || "entity_date",
        period: watch("period") || "days",
        duration: watch("duration") || "",
        starting_date: watch("starting_date") || "",
        finishing_date: watch("finishing_date") || "",
        unpaid: watch("unpaid") || false,
        has_invoice: watch("has_invoice") || false,
    };

    const disabled = usePaginationFilterEffects(
        filter,
        writtenFilter,
        getAllCashRegisters,
        setCashRegisters,
        setFilter,
        refreshBool,
    );

    // Table columns configuration
    const columns = [
        { label: "Paciente", icon: faUser, width: "w-[24%]" },
        {
            label: "Servicio",
            icon: faFileInvoice,
            cellClass: "hidden xl:table-cell",
            width: "xl:w-[24%]",
        },
        {
            label: (
                <>
                    Fecha{" "}
                    <div
                        className="tooltip text-md"
                        data-tip="Fecha de la sesión, clase o entrada en vigor del bono"
                    >
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </div>
                </>
            ),
            icon: faCalendar,
            width: "w-[11%]",
        },
        {
            label: (
                <>
                    Creación{" "}
                    <div className="tooltip text-md" data-tip="Fecha de creación del albarán">
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </div>
                </>
            ),
            icon: faCalendar,
            width: "w-[11%]",
        },
        { label: "Precio", icon: faEuroSign, width: "w-[10%]" },
        { label: "Deuda", icon: faEuroSign, width: "w-[10%]" },
    ];

    // Function to get row data for table
    const getRowData = (cashRegister, id) => {
        const isOverdue = parseFloat(cashRegister.debt) > 0;
        const debtVariant = isOverdue ? "warning" : "success";
        const debtText = isOverdue ? `${parseFloat(cashRegister.debt).toFixed(2)}€` : "Pagado";
        const fullName = `${cashRegister.patient_name} ${cashRegister.patient_surnames}`;

        return [
            // Paciente
            <div
                key={`${id}-patient`}
                className="font-semibold text-base-content truncate"
                title={fullName}
            >
                {fullName}
            </div>,
            // Servicio
            <span
                key={`${id}-service`}
                className="text-base-content/70 truncate"
                title={cashRegister.name}
            >
                {cashRegister.name}
            </span>,
            // Fecha de servicio
            <span key={`${id}-entity-date`} className="text-base-content/70">
                {cashRegister.entity_date}
            </span>,
            // Fecha de creación
            <span key={`${id}-issue-date`} className="text-base-content/70">
                {cashRegister.issue_date}
            </span>,
            // Precio
            <span key={`${id}-price`} className="text-base-content/70">
                {parseFloat(cashRegister.final_price).toFixed(2)}€
            </span>,
            // Deuda
            <TableBadge key={`${id}-debt`} variant={debtVariant} size="sm" className="text-sm">
                {debtText}
            </TableBadge>,
        ];
    };

    // Function to get detail path (for table actions)
    const getDetailPath = (id) => `/cash-register/${id}`;

    // Custom actions renderer for cash register table
    const renderActions = (cashRegister, id) =>
        renderCashRegisterDropdown(
            cashRegister,
            id,
            "sm",
            navigate,
            dispatch,
            disabled,
            refreshBool,
            setRefreshBool,
            clinicId,
        );

    // Card render functions
    const renderTitle = (cashRegister) =>
        `${cashRegister.patient_name} ${cashRegister.patient_surnames}`;

    const renderSubtitle = (cashRegister) => (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm mt-1">
            <span className="flex items-center gap-1">
                <FontAwesomeIcon icon={faFileInvoice} className="w-3 h-3 text-base-content/40" />
                {cashRegister.name}
            </span>
            <span className="text-base-content/40">•</span>
            <span className="flex items-center gap-1">
                <FontAwesomeIcon icon={faEuroSign} className="w-3 h-3 text-base-content/40" />
                {parseFloat(cashRegister.final_price).toFixed(2)}€
            </span>
        </div>
    );

    const renderFooter = (cashRegister) => {
        const isOverdue = parseFloat(cashRegister.debt) > 0;
        const debtVariant = isOverdue ? "warning" : "success";
        const debtText = isOverdue ? `${parseFloat(cashRegister.debt).toFixed(2)}€` : "Pagado";

        return (
            <div className="flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-base-content/70">
                    <span className="flex items-center gap-1">
                        <FontAwesomeIcon
                            icon={faCalendar}
                            className="w-3 h-3 text-base-content/40"
                        />
                        {cashRegister.entity_date}
                    </span>
                    <span className="text-base-content/40 hidden sm:inline">•</span>
                    <span className="hidden sm:inline">Creado: {cashRegister.issue_date}</span>
                </div>
                <TableBadge variant={debtVariant} size="sm">
                    {debtText}
                </TableBadge>
            </div>
        );
    };

    // Custom actions renderer for mobile cards (smaller dropdown)
    const renderCardActions = (cashRegister, id) =>
        renderCashRegisterDropdown(
            cashRegister,
            id,
            "xs",
            navigate,
            dispatch,
            disabled,
            refreshBool,
            setRefreshBool,
            clinicId,
        );

    const formConfig = getFormConfig(writtenFilter);
    const tableState = getTableState(cashRegisters);

    const handleDownload = handleSubmitWithDispatch(dispatch, downloadCashRegisters, {
        ...filter,
        ...writtenFilter,
        advanced_temporality:
            isDefined(writtenFilter.temporality) && writtenFilter.temporality !== "all",
    });

    const renderContent = () => {
        if (tableState === "loading") {
            return <LoadingTable message="Cargando albaranes..." />;
        }

        if (tableState === "empty") {
            return (
                <EmptyTable
                    icon={faFileInvoice}
                    title="No hay albaranes disponibles"
                    description="Los albaranes aparecerán aquí una vez que se registren servicios."
                />
            );
        }

        return (
            <>
                <OverviewCardList
                    data={cashRegisters}
                    navigate={navigate}
                    getDetailPath={getDetailPath}
                    renderTitle={renderTitle}
                    renderSubtitle={renderSubtitle}
                    renderFooter={renderFooter}
                    renderActions={renderCardActions}
                />
                <OverviewTable
                    data={cashRegisters}
                    navigate={navigate}
                    columns={columns}
                    getRowData={getRowData}
                    getDetailPath={getDetailPath}
                    renderActions={renderActions}
                    actionsWidth="w-[10%]"
                />
            </>
        );
    };

    return (
        <div className="sm:container p-3 mx-auto mt-5 mb-12">
            <h2 className="sr-only">
                Consulta los albaranes de tu clínica de fisioterapia en MiFisio Agenda - Albaranes
            </h2>

            <Collapse isMainTitle={true} title="Albaranes" faModality="filter" initialState={true}>
                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />
                </form>
            </Collapse>

            <div className="mt-6">
                <div className="my-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                    {/* Download button */}
                    <button
                        className="btn btn-primary btn-sm w-full sm:w-auto"
                        onClick={handleDownload}
                        disabled={cashRegisters.pagination?.total_items < 1}
                    >
                        <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
                        Descargar albaranes
                        <div
                            className="tooltip tooltip-top z-50"
                            data-tip="Se descargarán los albaranes resultantes de los filtros aplicados"
                        >
                            <FontAwesomeIcon icon={faCircleInfo} className="ml-2" />
                        </div>
                    </button>

                    {/* Pagination */}
                    <div className="w-full sm:w-auto">
                        <PaginatorComponent
                            pagination={cashRegisters.pagination}
                            setFilter={setFilter}
                            filter={filter}
                            disabled={disabled}
                        />
                    </div>
                </div>

                {renderContent()}

                {/* Pagination for small screens - duplicated to improve UX */}
                <div className="w-full my-5 sm:w-auto lg:hidden">
                    <PaginatorComponent
                        pagination={cashRegisters.pagination}
                        setFilter={setFilter}
                        filter={filter}
                        disabled={disabled}
                        showTotalItems={false}
                    />
                </div>
            </div>
        </div>
    );
};

// Helper function to create dropdown items for cash register actions
const createCashRegisterDropdownItems = ({
    cashRegister,
    id,
    navigate,
    dispatch,
    disabled,
    refreshBool,
    setRefreshBool,
    clinicId,
}) => {
    const hasPaymentActions =
        Number(cashRegister.debt) > 0 ||
        Number(cashRegister.final_price) - Number(cashRegister.debt) > 0;
    const hasInvoice = isDefined(cashRegister.invoice_id);

    const items = [
        {
            key: "details",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faEye} className="mr-2" />
                    Detalles
                </div>
            ),
            onClick: handleSubmit(navigate, `/cash-register/${id}`),
        },
    ];

    // Add payment action if applicable
    if (hasPaymentActions) {
        items.push({
            key: "payment",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faEuroSign} className="mr-2" />
                    {Number(cashRegister.debt) > 0 ? "Pago rápido" : "Devolución rápida"}
                </div>
            ),
            custom: (
                <RegisterChargePopup
                    originalValues={cashRegister}
                    refresh={handleSubmit(setRefreshBool, !refreshBool)}
                    trigger={
                        <div className="flex items-center w-full px-3 py-2 text-sm text-base-content cursor-pointer rounded-md transition-colors duration-150 ease-in-out hover:bg-primary/8">
                            <FontAwesomeIcon icon={faEuroSign} className="mr-2" />
                            {Number(cashRegister.debt) > 0 ? "Pago rápido" : "Devolución rápida"}
                        </div>
                    }
                />
            ),
        });
    }

    // Add create invoice action if no invoice exists
    if (!hasInvoice) {
        items.push({
            key: "create-invoice",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
                    Crear Factura
                </div>
            ),
            onClick: handleSubmitWithDispatch(
                dispatch,
                postInvoice,
                clinicId,
                id,
                handleSubmit(setRefreshBool, !refreshBool),
            ),
            disabled,
        });
    }

    // Add invoice actions if invoice exists
    if (hasInvoice) {
        items.push(
            {
                key: "download-invoice",
                label: (
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Descargar Factura
                    </div>
                ),
                onClick: () =>
                    handleSubmitWithDispatch(dispatch, downloadInvoice, cashRegister.invoice_id)(),
            },
            {
                key: "send-invoice",
                label: (
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                        Enviar Factura{" "}
                        <div
                            className="tooltip tooltip-left tooltip-primary ml-1 z-[100]"
                            data-tip="La factura se enviará al correo asociado al paciente"
                        >
                            <FontAwesomeIcon icon={faCircleInfo} className="text-primary" />
                        </div>
                    </div>
                ),
                onClick: () =>
                    handleSubmitWithDispatch(dispatch, sendInvoiceEmail, cashRegister.invoice_id)(),
            },
        );
    }

    return items;
};

export default OverviewCashRegister;
